import React, { FC } from 'react';
import {TextDisplay, TextDisplayProps} from "./TextDisplay";
import {Tooltip} from "@mui/material";
import {NumberDisplay} from "./NumberDisplay";
import {MoneyDisplay} from "./MoneyDisplay";

/**
 * A representation of a number to be displayed with optional alignment and tooltip support.
 *
 * This type is used to define items where numbers need to be displayed side by side,
 * often in a UI component, with additional metadata for alignment and context.
 *
 * @property value - The numeric value to be displayed. This is the core data for the item.
 * @property tooltip - An optional string providing additional context or explanation for the number.
 *                     If undefined, no tooltip will be displayed.
 * @property align - An optional alignment setting determining how the number is positioned.
 *                   Accepted values are "start", "end", or "center". Defaults to undefined,
 *                   leaving alignment to the consumer's discretion.
 * @property isMoney - An optional flag indicating if the number should be formatted as currency.
 *                     If true, the number will be formatted with currency symbols and appropriate precision.
 */
export type NumbersSideBySideItem = {
    value: number
    tooltip?: string
    align?: "start" | "end" | "center"
}

/**
 * Defines the structure for displaying two numeric-based items side by side.
 *
 * This is used to render and manage the visual or logical representation
 * of a paired comparison between two numeric entities. It extends
 * `TextDisplayProps` to include additional properties specific to the
 * numbers being displayed.
 */
export interface NumbersSideBySideProps extends TextDisplayProps {
    isMoney?: boolean
    /** item on left side */
    left: NumbersSideBySideItem
    /** item on right side */
    right: NumbersSideBySideItem
}

/**
 * Renders two numerical values side by side, optionally formatted as monetary values, with customizable alignment.
 *
 * This function is used to visually display two numeric values (or monetary values) side by side, with a separator
 * between them. It supports customization of alignment and format based on the provided properties.
 *
 * @param props Configuration for rendering the numbers or monetary values. The object should include:
 * - `left`: An object representing the first value to display. Must include a `value` property.
 * - `right`: An object representing the second value to display. Must include a `value` property.
 * - `align` (optional): A string specifying horizontal alignment. Defaults to `"center"`. Accepted values are `"left"`, `"center"`, or `"right"`.
 * - `isMoney`: A boolean indicating whether the values should be displayed in a monetary format. If `true`, formats the values as monetary amounts.
 * - Other custom properties passed to inner components.
 *
 * @return Returns a JSX element containing the two values rendered side by side with a separator. If `isMoney` is true, the values are formatted using `MoneyDisplay`. Otherwise, `NumberDisplay` is used.
 *
 * @throws Will throw an error if the `props` object is missing required properties such as `left.value` or `right.value`.
 */
export function NumbersSideBySide( props: NumbersSideBySideProps ) {
    const { left, right } = props;
    const alignment = props.align || "center";

    if( props.isMoney ) {
        return (
            <div className='flex' style={{ justifyContent: alignment }}>
                <MoneyDisplay { ...props } value={ left.value } />
                <TextDisplay { ...props } style={{ paddingLeft: 3+'px', paddingRight: 3+'px' }}> | </TextDisplay>
                <MoneyDisplay { ...props } value={ right.value } />
            </div>
        );
    }
    else {
        return (
            <div className='flex' style={{ justifyContent: alignment }}>
                <NumberDisplay { ...props } value={ left.value } />
                <TextDisplay { ...props } style={{ paddingLeft: 3+'px', paddingRight: 3+'px' }}> | </TextDisplay>
                <NumberDisplay { ...props } value={ right.value } />
            </div>
        );
    }


}